import { useState, memo } from 'react';
import FJImageItemIconBox from './FJImageItemIconBox';
import ReloadIcon from '../../../assets/svg/reload.svg?react';
import FJResourceItemLoading from '../FJResourceItemLoading/FJResourceItemLoading';
import { useCallback, useEffect, useRef, useMemo } from 'react';
import classNames from 'classnames';
import styles from './FJImageItemPreview.module.less';
import FJSlideCompare from '../FJSlideCompare/FJSlideCompare';

const FJImageItemPreview = memo(function FJImageItemPreview(props) {
    const {
        imgUrl,
        originalUrl,

        isShowSlideCompare = false,
        isShowComparison = false,
        isShowDeleteIcon = false,
        isShowEditIcon = false,

        onDelete,
        shareToSociety,
        onEdit,
    } = props;

    useEffect(() => {
        setStateImgUrl(imgUrl);
        setStateIsLoading(true);
        setStateCompareLineScale(0.3);
    }, [imgUrl]);

    useEffect(() => {
        setStateOriginImgUrl(originalUrl);
        setStateOriginLoadingSuccess(false);
    }, [originalUrl]);

    const [stateImgUrl, setStateImgUrl] = useState(imgUrl);
    const [stateOriginImgUrl, setStateOriginImgUrl] = useState(originalUrl);

    // 是否显示对比
    const [stateIsComparison, setStateIsComparison] = useState(false);

    // 图片加载
    const [stateIsLoading, setStateIsLoading] = useState(false);
    const [stateIsLoadingError, setStateIsLoadingError] = useState(false);

    // 原图加载
    const [stateOriginLoadingSuccess, setStateOriginLoadingSuccess] = useState(false);
    const [stateOriginLoadingError, setStateOriginLoadingError] = useState(false);

    const [stateOriginImageSize, setStateOriginImageSize] = useState({ width: 0, height: 0 });
    const [stateImageSize, setStateImageSize] = useState({ width: 0, height: 0 });
    const [stateCompareLineScale, setStateCompareLineScale] = useState(0.3);

    const [editImageRect, setEditImageRect] = useState({ left: 0, top: 0, width: 0, height: 0 });
    const [originImageStyle, setOriginImageStyle] = useState({});
    const [afterImageStyle, setAfterImageStyle] = useState({});

    const imageRef = useRef(null);
    const originImageRef = useRef(null);
    const imgBoxRef = useRef(null);

    const __getReloadNewUrl = useCallback(url => {
        const key = url.indexOf('?') !== -1 ? '&' : '?';
        return `${url + key}v=${new Date().getTime()}`;
    }, []);

    const _onReload = useCallback(
        e => {
            e.stopPropagation();
            if (stateIsLoadingError) {
                setStateImgUrl(__getReloadNewUrl(imgUrl));
                setStateIsLoading(true);
            }
            if (stateOriginLoadingSuccess) {
                setStateOriginImgUrl(__getReloadNewUrl(originalUrl));
            }
        },
        [imgUrl, originalUrl, stateIsLoadingError, stateOriginLoadingSuccess, __getReloadNewUrl],
    );

    useEffect(() => {
        if (!isShowSlideCompare) return;
        if (stateOriginLoadingSuccess && originImageRef.current) {
            const { naturalWidth: originWidth, naturalHeight: originHeight } = originImageRef.current;
            setStateOriginImageSize({
                width: originWidth,
                height: originHeight,
            });
        }
        if (!stateIsLoading && imageRef.current) {
            const { naturalWidth, naturalHeight } = imageRef.current;
            setStateImageSize({
                width: naturalWidth,
                height: naturalHeight,
            });
        }
    }, [stateOriginLoadingSuccess, stateIsLoading, isShowSlideCompare]);

    const slideCompareActive = useMemo(() => {
        return (
            isShowSlideCompare &&
            !(stateIsLoading || stateOriginLoadingError) &&
            editImageRect.width > 0 &&
            editImageRect.height > 0
        );
    }, [isShowSlideCompare, stateIsLoading, stateOriginLoadingError, editImageRect]);

    const _getEditImageRect = useCallback(() => {
        if (!isShowSlideCompare || !stateOriginLoadingSuccess || !stateImageSize.width || !stateImageSize.height) {
            return { left: 0, top: 0, width: 0, height: 0 };
        }
        const { width: boxWidth, height: boxHeight } = imgBoxRef.current?.getBoundingClientRect() || {};

        const ratio = stateImageSize.width / stateImageSize.height;

        const width = Math.min(boxWidth, boxHeight * ratio);
        const height = Math.min(boxHeight, boxWidth / ratio);

        const left = Math.max(0, (boxWidth - width) / 2);
        const top = Math.max(0, (boxHeight - height) / 2);

        return {
            x: left,
            y: top,
            width,
            height,
        };
    }, [stateOriginLoadingSuccess, stateImageSize, isShowSlideCompare]);

    const _updateOriginImageStyle = useCallback(
        editImageRect => {
            if (isShowComparison || !slideCompareActive) {
                setOriginImageStyle({ opacity: stateIsComparison ? 1 : 0 });
                return;
            }

            if (!editImageRect.width || !editImageRect.height) {
                setOriginImageStyle({});
                return;
            }
            const { width: boxWidth } = imgBoxRef.current?.getBoundingClientRect() || {};

            setOriginImageStyle({
                clipPath: `inset(0px ${(boxWidth - editImageRect.width) / 2 + editImageRect.width * (1 - stateCompareLineScale)}px 0px 0px)`,
            });
        },
        [stateCompareLineScale, isShowComparison, stateIsComparison, slideCompareActive],
    );

    const _updateAfterImageStyle = useCallback(
        editImageRect => {
            if (isShowComparison || !slideCompareActive) {
                setAfterImageStyle({ opacity: stateIsComparison ? 0 : 1 });
                return;
            }

            if (!editImageRect.width || !editImageRect.height) {
                setAfterImageStyle({});
                return;
            }
            setAfterImageStyle({
                clipPath: `inset( 0px 0px 0px ${editImageRect.width * stateCompareLineScale + editImageRect.x}px)`,
            });
        },
        [stateCompareLineScale, isShowComparison, stateIsComparison, slideCompareActive],
    );

    const _updateCompareLineData = useCallback(() => {
        const newEditImageRect = _getEditImageRect();
        _updateOriginImageStyle(newEditImageRect);
        _updateAfterImageStyle(newEditImageRect);
        setEditImageRect(newEditImageRect);
    }, [_getEditImageRect, _updateOriginImageStyle, _updateAfterImageStyle]);

    useEffect(() => {
        _updateCompareLineData();

        window.addEventListener('resize', _updateCompareLineData);
        return () => {
            window.removeEventListener('resize', _updateCompareLineData);
        };
    }, [_updateCompareLineData]);

    return (
        <div className={styles.box} ref={imgBoxRef}>
            <div className={styles.main_img_box} style={afterImageStyle}>
                <img
                    ref={imageRef}
                    className={classNames(styles.main_img)}
                    src={stateImgUrl}
                    alt=''
                    onLoad={() => {
                        setStateIsLoadingError(false);
                        setStateIsLoading(false);
                    }}
                    onError={() => {
                        setStateIsLoadingError(true);
                        setStateIsLoading(false);
                    }}
                    loading='lazy'
                />
            </div>
            <div className={styles.comparison_img_box} style={originImageStyle}>
                <img
                    ref={originImageRef}
                    className={classNames(styles.comparison_img)}
                    src={stateOriginImgUrl}
                    alt=''
                    onLoad={() => {
                        setStateOriginLoadingError(false);
                        // 延迟150ms更新对比数据，防止图片加载过快导致对比数据更新错误
                        setTimeout(() => {
                            setStateOriginLoadingSuccess(true);
                        }, 200);
                    }}
                    onError={() => {
                        setStateOriginLoadingSuccess(false);
                        setStateOriginLoadingError(true);
                    }}
                    loading='lazy'
                />
            </div>

            <FJSlideCompare
                className={styles.compare_box}
                editImageRect={editImageRect}
                compareLineScale={stateCompareLineScale}
                originImageSize={stateOriginImageSize}
                editImageSize={stateImageSize}
                active={slideCompareActive}
                zoom={1}
                changeCompareLineScale={setStateCompareLineScale}
            />

            <FJImageItemIconBox
                onDelete={onDelete}
                shareToSociety={shareToSociety}
                isShowDeleteIcon={isShowDeleteIcon}
                isShowComparison={isShowComparison}
                imageLoadingError={stateIsLoadingError || stateOriginLoadingError}
                disabledComparison={stateIsComparison || !stateOriginLoadingSuccess}
                isComparisonActive={stateIsComparison}
                isShowEditIcon={isShowEditIcon}
                reLoadImage={_onReload}
                changeComparison={setStateIsComparison}
                onEdit={onEdit}
            />

            {stateIsLoadingError && (
                <div className={styles.retry_box}>
                    <ReloadIcon className={styles.reload_icon} onClick={_onReload} />
                </div>
            )}
            {stateIsLoading && <FJResourceItemLoading className={styles.loading} />}
        </div>
    );
});
export default FJImageItemPreview;
